import { createI18n } from 'vue-i18n'
import { watch } from 'vue'
import type {
  DateTimeFormatKey,
  I18nKey,
  NumberFormatKey,
} from '@/locales/i18n.types'
import {
  datetimeFormats,
  numberFormats,
} from '@team-milva/lab-constants/i18nFormats'
import { ENV, HOST } from '@/globals/typescript/utils/host'
import { merge } from 'lodash-es'

// Mirrors @team-milva/lab-models' full TLabCustomerLocale/LAB_CUSTOMER_LANGUAGES
// set — @team-milva/lab-constants' i18nFormats (datetimeFormats/numberFormats,
// passed into createI18n below) now defines all 5, which is what vue-i18n
// infers its Locales generic from. A local, narrower type is still used here
// (rather than importing TLabCustomerLocale directly) so this app's actual
// i18n-runtime ceiling stays a single, explicit source of truth — see
// "Per-Lab & Per-Customer Language Selection", Phase 4.
export const SUPPORTED_LOCALES = ['en', 'da', 'de', 'no', 'fr'] as const
export type TAppLocale = (typeof SUPPORTED_LOCALES)[number]

const I18N_HOST =
  (import.meta.env.VITE_I18N_HOST as string | undefined) ??
  (ENV.isLocalhost ? '' : HOST.translations)

// Only warn once per key for the "missing everywhere" case
const warnedMissingEverywhere = new Set<string>()

export const i18n = createI18n({
  legacy: false as const,
  globalInjection: true as const,
  locale: 'en' as TAppLocale,
  fallbackLocale: 'en' as TAppLocale,

  missingWarn: false,
  fallbackWarn: false,

  numberFormats,
  datetimeFormats,

  missing: (locale, key) => {
    if (key.startsWith('W: ')) return key.replace(/^W:\s*/, '')

    if (ENV.isDev) {
      const existsSomewhere = SUPPORTED_LOCALES.some((l) =>
        i18n.global.te(key, l)
      )

      if (
        key.includes(' ') &&
        !existsSomewhere &&
        !warnedMissingEverywhere.has(key)
      ) {
        warnedMissingEverywhere.add(key)
        console.warn(`!! Remember to prefix temporary keys with "W: " (${key})`)
        return key
      }

      console.warn(`Not found '${key}' key in '${locale}' locale messages.`)
    }

    return key
  },
})

// <html lang> stays in sync with the active locale, wherever it's set
// (LangSelect.vue, userStore.ts, App.vue, main.ts) — one watcher here covers
// every call site instead of patching each. This is also what lets CSS
// `hyphens: auto` pick the right dictionary; see base.css's long-word-wrap
// comment.
watch(
  i18n.global.locale,
  (locale) => {
    document.documentElement.lang = locale
  },
  { immediate: true }
)

export const t = (key: I18nKey, params?: Record<string, unknown>) =>
  params === undefined ? i18n.global.t(key) : i18n.global.t(key, params)

export const n = (
  value: number,
  format?: NumberFormatKey | Intl.NumberFormatOptions,
  locale?: TAppLocale
) =>
  locale !== undefined
    ? i18n.global.n(value, format as string, locale)
    : format !== undefined
      ? i18n.global.n(value, format as string)
      : i18n.global.n(value)

export const d = (
  value: Date | number,
  format: DateTimeFormatKey,
  localeOrOptions?: TAppLocale | Intl.DateTimeFormatOptions,
  maybeOptions?: Intl.DateTimeFormatOptions
) => {
  // Case: d(value, key, options)
  if (typeof localeOrOptions === 'object') {
    return i18n.global.d(value, {
      ...(i18n.global.getDateTimeFormat(format) as Intl.DateTimeFormatOptions),
      ...localeOrOptions,
    })
  }

  // Case: d(value, key, locale, options)
  if (typeof localeOrOptions === 'string') {
    return i18n.global.d(
      value,
      {
        ...(i18n.global.getDateTimeFormat(
          format
        ) as Intl.DateTimeFormatOptions),
        ...maybeOptions,
      },
      localeOrOptions
    )
  }

  // Case: d(value, key)
  return i18n.global.d(value, format)
}

/**
 * Fetch latest i18n version and load general messages for all locales in SUPPORTED_LOCALES.
 * Uses remote-only messages (no local/test overlays). Falls back silently on error.
 * When @team-milva/i18n-keys is locally linked, loads from the virtual module (raw JSON files)
 * instead of the CDN — no env var needed.
 */
export async function loadRemoteI18n(): Promise<void> {
  try {
    if (import.meta.env.DEV) {
      try {
        const mod = await import('@team-milva/i18n-keys/latest-translations')
        if ((mod as { __isLocalBundle__?: boolean }).__isLocalBundle__) {
          const { latestTranslations } = mod
          const localGeneral = latestTranslations.translations.general ?? {}
          const localLabCustomer =
            latestTranslations.translations['lab-customer'] ?? {}
          for (const locale of SUPPORTED_LOCALES) {
            const merged = merge(
              {},
              (localGeneral[locale] ?? {}) as Record<string, unknown>,
              (localLabCustomer[locale] ?? {}) as Record<string, unknown>
            ) as Record<string, unknown>
            i18n.global.setLocaleMessage(locale, merged)
          }
          return
        }
      } catch {
        // local bundle not available — fall through to CDN
      }
    }

    const latestRes = await fetch(`${I18N_HOST}/i18n/latest.json`, {
      cache: 'no-store',
    })
    if (!latestRes.ok) throw new Error(`latest.json ${latestRes.status}`)
    const latest = await latestRes.json()
    const version: string =
      typeof latest === 'string'
        ? latest
        : typeof latest?.version === 'string'
          ? latest.version
          : ''
    if (!version) throw new Error('Missing version in latest.json')

    const localeResults = await Promise.allSettled(
      SUPPORTED_LOCALES.map(async (locale) => {
        const [generalRes, labCustomerRes] = await Promise.all([
          fetch(`${I18N_HOST}/i18n/${version}/general/${locale}.json`),
          fetch(`${I18N_HOST}/i18n/${version}/lab-customer/${locale}.json`).catch(
            () => null
          ),
        ])
        if (!generalRes.ok) throw new Error('Failed locale fetch')
        const general = await generalRes.json()
        const labCustomer =
          labCustomerRes?.ok ? await labCustomerRes.json() : {}
        return {
          locale,
          merged: merge({}, general, labCustomer) as Record<string, unknown>,
        }
      })
    )

    for (const [i, result] of localeResults.entries()) {
      if (result.status === 'fulfilled') {
        i18n.global.setLocaleMessage(result.value.locale, result.value.merged)
      } else if (ENV.isDev) {
        console.warn(
          `[i18n] Failed to load locale '${SUPPORTED_LOCALES[i]}':`,
          result.reason
        )
      }
    }
  } catch (err) {
    if (ENV.isDev) {
      console.warn('[i18n] Remote/local load skipped:', err)
    }
    // Fallback: keep existing local messages
  }
}

